import React, { Fragment, useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useParams } from "react-router-dom"

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import { Grid } from "@mui/material"
import { Button, InputText } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"
import { useFormikContext } from "formik"

import { UploadFileBlock } from "../../components/form/UploadFileBlock"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import { Operators } from "../../types/custom/projects"
import type { ConditionListFile } from "../../types/generated/api/ConditionListFile"
import { DataFileGroup } from "../../types/generated/api/DataFileGroup"
import { multiValuesOperators, operatorsBasedOnFeatureType } from "../../utils/conditionHelpers"
import { FILE_ID_MATCHER, matchFile } from "../../utils/rulesetHelpers"
import { DataFilesMenu } from "../Ruleset/components/DataFilesMenu"
import { DataBlock } from "../Ruleset/components/RuleCard"
import { ArrowAdornment } from "../Scorecards/components/Condition"
import { RemoveFromBuckets, createScoreTableBucketsFromResultsArray } from "./helpers"
import type { ConditionProps, InputWithReadModeProps, ScoreTableFormik } from "./interfaces"

/**
 * Renders an input component with read mode functionality.
 *
 * @param props - The component props.
 * @param id - The ID of the input.
 * @param value - The value of the input.
 * @param isReadMode - (Optional)Determines if the input is in read mode. Defaults to false.
 * @param isSelect - (Optional) Determines if the input is a select input. Defaults to false.
 * @param placeHolder - (Optional) The placeholder text for the input.
 * @param enableUpload - (Optional) Determines if file upload is enabled. Defaults to false.
 * @param handleChange - (Optional) The event handler for input change.
 * @param handleBlur - (Optional) The event handler for input blur.
 * @param isDisabled - (Optional) Determines if the input is disabled. Defaults to false.
 * @returns The rendered input component.
 */
function InputWithReadMode(props: Readonly<InputWithReadModeProps>): React.ReactElement {
  const {
    id,
    isReadMode = false,
    isSelect = false,
    type,
    value,
    placeHolder,
    enableUpload = false,
    handleChange,
    handleBlur,
    isDisabled = false,
    options,
    fileUploadHelpers,
  } = props
  const { id: projectId } = useParams<{ id: string }>()

  const abortControllerRef = useRef<AbortController | null>(null)

  const [filesMenuAnchor, setFilesMenuAnchor] = useState<null | HTMLElement>(null)
  const featuresMenuRef = useRef<HTMLInputElement>(null)

  const {
    isLoading: isCsvUploading,
    mutateAsync: uploadCSVAsyncMutation,
    reset,
  } = useMutation<DataFileGroup, AxiosError, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.uploadDataFile({
        project_uuid: projectId as string,
        file: file,
        signal: abortControllerRef.current.signal,
        type: DataFileGroup.type.CONDITION_LIST,
      })
    },
    {
      mutationKey: "uploadCSV",
      onSuccess: async (response) => {
        fileUploadHelpers?.handleUploadFileSuccessful({
          name: response?.name,
          uuid: response?.uuid,
          version: response.active_version.version,
        })
      },
    },
  )

  // handle close dropzone
  const handleCloseAndAbort = useCallback(() => {
    abortControllerRef.current?.abort()
    reset()
  }, [reset])

  // handle upload once files are accepted
  const handleUpload = async (file: File): Promise<void> => {
    try {
      await uploadCSVAsyncMutation(file)

      // Invalidate training data to trigger a refresh
      await queryClient.invalidateQueries(["data-files", projectId])
    } catch {
      // reset dropzone on error
      handleCloseAndAbort()
    }
  }

  /**
   * indicator for displaying the file block that contains the file name used in condition
   */
  const shouldDisplayFileBlock = Boolean((matchFile(value).isMatching || isCsvUploading) && enableUpload && !isReadMode)

  if (shouldDisplayFileBlock)
    return (
      <UploadFileBlock
        onDelete={() => {
          reset()
          fileUploadHelpers?.handleDeleteFile(matchFile(value).fileId as string)
        }}
        fileName={
          fileUploadHelpers?.criteria_files?.find(
            (file: { uuid: string; name: string }) => file.uuid === matchFile(value).fileId,
          )?.name ?? ""
        }
        isUploading={isCsvUploading}
        handleCancel={handleCloseAndAbort}
        isReadMode={isReadMode}
        isDisabled={isCsvUploading}
        fileID={matchFile(value).fileId}
      />
    )

  if (isReadMode)
    return (
      <DataBlock
        value={
          matchFile(value).isMatching
            ? (fileUploadHelpers?.criteria_files?.find(
                (file: { uuid: string; name: string }) => file.uuid === matchFile(value).fileId,
              )?.name ?? "")
            : value
        }
        fileId={matchFile(value).isMatching ? matchFile(value).fileId : undefined}
      />
    )

  if (enableUpload)
    return (
      <Fragment>
        <InputText
          id={id}
          type={type}
          anchoringRef={featuresMenuRef}
          hideDescription
          isSelect={isSelect}
          options={options}
          placeholder={placeHolder}
          value={value}
          handleChange={handleChange}
          handleBlur={handleBlur}
          disabled={isDisabled}
          endAdornment={
            enableUpload && !isSelect ? (
              <ArrowAdornment
                anchorEl={filesMenuAnchor}
                onClick={() => setFilesMenuAnchor(!!filesMenuAnchor ? null : featuresMenuRef?.current)}
                style={{ color: "var(--base-text-2)" }}
              />
            ) : undefined
          }
          fullWidth
          menuProps={{ menuMaxContent: true }}
        />
        {filesMenuAnchor && (
          <DataFilesMenu
            onDataFileUpload={handleUpload}
            onAddDataFile={(file) =>
              fileUploadHelpers?.handleUploadFileSuccessful({ name: file.name, uuid: file.uuid, version: "" })
            }
            anchorEl={filesMenuAnchor}
            onClose={() => setFilesMenuAnchor(null)}
          />
        )}
      </Fragment>
    )

  return (
    <InputText
      id={id}
      type={type}
      hideDescription
      isSelect={isSelect}
      options={options}
      placeholder={placeHolder}
      value={value}
      handleChange={handleChange}
      handleBlur={handleBlur}
      disabled={isDisabled}
      fullWidth
      menuProps={{ menuMaxContent: true }}
    />
  )
}

export function Condition(props: Readonly<ConditionProps>): React.ReactElement {
  const { mode, configIndex, conditionIndex } = props

  const { t } = useTranslation("scorecards")

  const { values, setFieldValue, handleChange, handleBlur, setValues, isSubmitting } =
    useFormikContext<ScoreTableFormik>()

  const conditionObject = values.criteria[configIndex]?.[conditionIndex]
  const conditionObjectId = `criteria[${configIndex}][${conditionIndex}]`

  const types = ["string", "number", "true", "false", "null"]

  const handleDeleteCriteria = (): void => {
    const buckets = createScoreTableBucketsFromResultsArray(values.results as number[], values.criteria)

    RemoveFromBuckets(buckets, configIndex, conditionIndex)

    setValues((prev) => {
      const deletedFileId = prev.criteria[configIndex]?.[conditionIndex]?.value?.match(FILE_ID_MATCHER)?.[1]

      if (deletedFileId) {
        prev.criteria_files = prev.criteria_files.filter((file) => !deletedFileId.includes(file.uuid))
      }

      prev.results = buckets.flat(values.criteria.length)

      if (!prev.criteria[configIndex]) return { ...prev }

      prev.criteria[configIndex] = prev.criteria[configIndex]?.filter((_, idx: number) => idx !== conditionIndex)

      return { ...prev }
    })
  }

  // function that accepts an operator translation and return its value from `operators` enum
  const getOpValuefromTranslation = (translation: string): string | undefined => {
    for (const value of Object.values(Operators)) {
      if (t(`operators.${value}`) === translation) {
        return value
      }
    }
  }

  return (
    <Grid container display={"flex"} gap={1}>
      <Grid
        item
        xs={
          multiValuesOperators.includes(conditionObject?.operator ?? "")
            ? 4
            : !["is null", "not null"].includes(conditionObject?.operator ?? "")
              ? 2
              : true
        }
      >
        <InputWithReadMode
          id={`${conditionObjectId}.operator`}
          isReadMode={mode === "read"}
          isSelect
          value={t(`operators.${conditionObject?.operator}`) ?? ""}
          handleChange={(e) => {
            ;["is null", "not null"].includes(e.target.value.toString()) &&
              setFieldValue(`${conditionObjectId}.type`, "null")
            e.target.value = getOpValuefromTranslation(e.target.value as string) as string
            handleChange(e)
          }}
          handleBlur={handleBlur}
          isDisabled={isSubmitting}
          options={operatorsBasedOnFeatureType.UNDEFINED.map((op) => t(`operators.${op}`))}
        />
      </Grid>

      {!multiValuesOperators.includes(conditionObject?.operator ?? "") &&
        !["is null", "not null"].includes(conditionObject?.operator ?? "") && (
          <Grid item xs={["string", "number"].includes(conditionObject?.type ?? "") ? 2 : true}>
            <InputWithReadMode
              id={`${conditionObjectId}.type`}
              isReadMode={mode === "read"}
              isSelect
              value={conditionObject?.type ?? ""}
              handleChange={handleChange}
              handleBlur={handleBlur}
              isDisabled={isSubmitting}
              options={types}
            />
          </Grid>
        )}

      {["string", "number"].includes(conditionObject?.type ?? "") &&
        !["is null", "not null"].includes(conditionObject?.operator ?? "") && (
          <Fragment>
            <Grid item xs>
              <InputWithReadMode
                id={`${conditionObjectId}.value`}
                isReadMode={mode === "read"}
                value={conditionObject?.value ?? ""}
                handleChange={handleChange}
                handleBlur={handleBlur}
                isDisabled={isSubmitting}
                type={conditionObject?.type === "number" ? "number" : undefined}
                enableUpload={multiValuesOperators.includes(conditionObject?.operator ?? "")}
                fileUploadHelpers={{
                  criteria_files: values.criteria_files,
                  handleDeleteFile: (uuid: string) => {
                    setFieldValue(
                      `criteria_files`,
                      values.criteria_files.filter((file: ConditionListFile) => file.uuid !== uuid),
                    )

                    setFieldValue(`criteria[${configIndex}][${conditionIndex}].value`, ``)
                  },
                  handleUploadFileSuccessful: (file) => {
                    setValues((prev) => {
                      prev.criteria_files = [...prev.criteria_files, file]
                      if (
                        !prev.criteria[configIndex]?.[conditionIndex]?.value &&
                        prev.criteria[configIndex]?.[conditionIndex]?.value != ""
                      )
                        return { ...prev }

                      prev.criteria[configIndex][conditionIndex].value = `$file<${file.uuid}>`

                      return { ...prev }
                    })
                  },
                }}
              />
            </Grid>

            {conditionObject?.operator === "between" && (
              <Fragment>
                <Grid item>
                  <DataBlock value={"AND"} />
                </Grid>

                <Grid item xs>
                  <InputWithReadMode
                    id={`${conditionObjectId}.secondValue`}
                    isReadMode={mode === "read"}
                    type="number"
                    value={conditionObject.secondValue as string}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    isDisabled={isSubmitting}
                  />
                </Grid>
              </Fragment>
            )}
          </Fragment>
        )}

      {mode !== "read" && (
        <Grid item>
          <Button
            variant="dangerous"
            size="regular"
            onClick={handleDeleteCriteria}
            disabled={isSubmitting || (values.criteria[configIndex] && values.criteria[configIndex].length <= 1)}
            tooltip={
              values.criteria[configIndex] && values.criteria[configIndex].length <= 1
                ? "Must have at least 1 condition per bucket"
                : undefined
            }
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </Button>
        </Grid>
      )}
    </Grid>
  )
}
